import { useNavigate } from 'react-router-dom';
import {
    ChangeEventHandler,
    FormEventHandler,
    useEffect,
    useState,
} from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import Constants from '../../../../../utils/Constants';
import { Policies } from '../constants/AuthConstants';
import useAuthContext from '../hooks/useAuthContext';
import { ICredentials, ICredentialsError } from '../interface/ICredentials';
import AuthPageContainer from '../components/AuthPageContainer';
import { useTranslation } from 'react-i18next';
import { commonFeatures } from '../../../featureFlags/commonFeatures';
import ProtectedSpinnerButton from '../../../../../components/common/ProtectedSpinnerButton';

const {
    isCA11388On_TermsOfServiceLoginScreen,
    enableAuth0Option,
    isCA14196On_RefreshTokenBugFix,
} = commonFeatures;

export const LoginPage = () => {
    const { t } = useTranslation('auth');
    const {
        localAuthLogin,
        renewLocalToken_deprecated,
        isAuthenticated,
        login,
        renewToken,
    } = useAuthContext();
    const navigate = useNavigate();

    const [isProcessing, setIsProcessing] = useState(false);
    const [credentials, setCredentials] = useState<ICredentials>({
        username: '',
        password: '',
    });
    const [credentialsError, setCredentialsError] = useState<ICredentialsError>(
        {
            username: false,
            password: false,
        },
    );

    const handleChange: ChangeEventHandler<HTMLInputElement> = event => {
        const { name, value } = event.currentTarget;

        const updatedCredentials = { ...credentials, [name]: value };
        setCredentials(updatedCredentials);

        const updatedCredentialsError = {
            ...credentialsError,
            [name]: value === '',
        };
        setCredentialsError(updatedCredentialsError);
    };

    const validateForm = (): boolean => {
        const credentialsErrorCopy = { ...credentialsError };
        Object.keys(credentials).forEach(field => {
            credentialsErrorCopy[field] = credentials[field] === '';
        });
        setCredentialsError(credentialsErrorCopy);

        return Object.values(credentialsErrorCopy).every(
            error => error === false,
        );
    };

    const handleSubmit: FormEventHandler<HTMLFormElement> = async event => {
        event.preventDefault();
        const isFormValid = validateForm();
        if (isFormValid) {
            setIsProcessing(true);
            await localAuthLogin({
                username: credentials.username,
                password: credentials.password,
                redirectPath: '/',
                isTransient: false,
            });
            setIsProcessing(false);
        }
    };

    useEffect(() => {
        if (
            isCA14196On_RefreshTokenBugFix &&
            localStorage.getItem(Constants.StorageItems.AUTH_TYPE) ===
                Constants.AuthType.LOCAL
        ) {
            const checkToken = async () => {
                const token = await renewToken();

                if (token && isAuthenticated(true)) {
                    navigate(Constants.Routes.FM_DASHBOARD);
                }
            };
            checkToken();
        }
    }, [isAuthenticated, navigate, renewToken]);

    useEffect(() => {
        if (
            !isCA14196On_RefreshTokenBugFix ||
            localStorage.getItem(Constants.StorageItems.AUTH_TYPE) ===
                Constants.AuthType.AUTH_0
        ) {
            const checkToken = async () => {
                const token = await renewLocalToken_deprecated();

                if (token && isAuthenticated(true)) {
                    navigate(Constants.Routes.FM_DASHBOARD);
                }
            };
            checkToken();
        }
    }, [isAuthenticated, navigate, renewLocalToken_deprecated]);

    return (
        <AuthPageContainer showNgatLogo={true}>
            <Form onSubmit={handleSubmit} noValidate className="px-4">
                <Col className="px-1">
                    <Form.Group>
                        <Form.Label className="mb-1">
                            {t('loginPage.email')}
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="username"
                            value={credentials.username}
                            onChange={handleChange}
                            className={
                                credentialsError.username ? 'border-danger' : ''
                            }
                            disabled={isProcessing}
                            autoComplete="username"
                        />
                    </Form.Group>
                    <br />
                    <Form.Group>
                        <Form.Label className="mb-1">
                            {t('loginPage.password')}
                        </Form.Label>
                        <Form.Control
                            type="password"
                            name="password"
                            value={credentials.password}
                            onChange={handleChange}
                            className={
                                credentialsError.password ? 'border-danger' : ''
                            }
                            disabled={isProcessing}
                            autoComplete="current-password"
                        />
                    </Form.Group>
                    <Row className="mt-3">
                        <a href={Constants.Routes.FORGOT_PASSWORD}>
                            {t('loginPage.forgotPassword')}
                        </a>
                    </Row>
                    <br />

                    <ProtectedSpinnerButton
                        type="submit"
                        size="lg"
                        variant="primary"
                        disabled={isProcessing}
                        className="fw-bold mb-1 px-4"
                        showSpinner={isProcessing}
                        buttonContent={t('loginPage.logIn')}
                        onClick={undefined}
                    />
                    <Row className="mt-3">
                        {isCA11388On_TermsOfServiceLoginScreen && (
                            <Col>
                                <p>
                                    <a
                                        href={`${Policies.nextGenAgTechLink}${Policies.termsOfServiceRoute}`}
                                    >
                                        {Policies.termsOfServiceText}
                                    </a>
                                </p>
                            </Col>
                        )}
                        {enableAuth0Option && (
                            <Col>
                                <p>
                                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                    <a
                                        href="#"
                                        onClick={event => {
                                            event.preventDefault();
                                            login();
                                        }}
                                    >
                                        {Constants.legacyLogin}
                                    </a>
                                </p>
                            </Col>
                        )}
                    </Row>
                </Col>
            </Form>
        </AuthPageContainer>
    );
};

export default LoginPage;
